import dayjs from "dayjs";

// Brand colors — kept in sync with the app's light-mode theme tokens (src/theme.js)
export const BRAND = {
  primary: "#1766A2", // MUI palette.primary.main
  primaryDark: "#123f5e", // primary[900]-ish
  primaryTint: "#eaf3fa", // primary[200]/[400] tint
  border: "#c7d9e6",
  green: "#22bf72", // greenAccent[500]
  greenTint: "#e9f9f1",
  red: "#d22836", // redAccent[500]
  amber: "#c98a13",
  amberTint: "#fdf3e2",
  text: "#1F2A40", // primary[900]
  muted: "#5b636f", // blueAccent[400]/grey
};

export const escapeHtml = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

// Money fields: always a formatted number, defaulting to 0.00 when null/missing.
export const formatMoney = (value) => {
  const num = Number(value);
  return (Number.isFinite(num) ? num : 0).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatMoneyRaw = (value) => {
  const num = Number(value);
  return (Number.isFinite(num) ? num : 0).toFixed(2);
};

// Non-money text fields: N/A when null/missing/blank.
export const formatText = (value) => {
  if (value === null || value === undefined) return "N/A";
  const str = String(value).trim();
  return str === "" ? "N/A" : escapeHtml(str);
};

export const formatTextRaw = (value) => {
  if (value === null || value === undefined) return "N/A";
  const str = String(value).trim();
  return str === "" ? "N/A" : str;
};

// For raw datetime values (e.g. "2026-06-28 20:21:35") that still need parsing.
export const formatDate = (value) => {
  if (!value) return "N/A";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY HH:mm") : String(value);
};

export const csvEscape = (value) => {
  const str = value === null || value === undefined ? "" : String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const toCsvLine = (cols) => cols.map(csvEscape).join(",");

// Rows normally arrive already shaped as { Format, Section, Data }. Older
// payload shapes wrapped the whole object as a JSON string under "Section" —
// keep that as a fallback so this keeps working if the API reverts.
export const parseSections = (rawResults) => {
  if (!rawResults) return [];
  const rows = JSON.parse(rawResults);
  return rows
    .map((row) => {
      if (row && typeof row.Data === "object" && row.Data !== null) {
        return row;
      }
      try {
        return JSON.parse(row.Section);
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean);
};

// Shared page chrome: toolbar (PDF/CSV buttons), sheet wrapper, base + print CSS.
// Callers supply the info box rows and the body sections as pre-built HTML.
export const renderStatementDocument = ({
  docTitle,
  pageTitle,
  generatedAt,
  infoRowsHtml,
  bodyHtml,
  csvContent,
  csvFileName,
  extraPrintCss = "",
}) => {
  const generatedLabel = dayjs(generatedAt).format("DD MMM YYYY HH:mm");
  const pdfFileName = (csvFileName || "Statement.csv").replace(/\.csv$/i, ".pdf");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>${escapeHtml(docTitle)}</title>
<script src="/assets/html2pdf.bundle.min.js"></script>
<script>
  if (typeof html2pdf === 'undefined') {
    var cdn = document.createElement('script');
    cdn.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
    document.head.appendChild(cdn);
  }
</script>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    margin: 0;
    padding: 24px;
    background: #f2f4f7;
    color: ${BRAND.text};
  }
  .toolbar {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    max-width: 1000px;
    margin: 0 auto 16px;
  }
  .toolbar button {
    border: none;
    border-radius: 6px;
    padding: 10px 18px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    color: #fff;
    transition: opacity 0.2s;
  }
  .toolbar button:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  #btn-pdf { background: ${BRAND.primary}; }
  #btn-csv { background: ${BRAND.green}; }
  .sheet {
    max-width: 1000px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid ${BRAND.border};
    border-radius: 8px;
    padding: 32px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  }
  .sheet.generating-pdf {
    box-shadow: none !important;
    border: none !important;
  }
  .doc-meta {
    text-align: right;
    font-size: 0.7rem;
    color: ${BRAND.muted};
    margin-bottom: 8px;
  }
  .statement-title {
    text-align: center;
    color: ${BRAND.primary};
    font-size: 1.5rem;
    letter-spacing: 1px;
    margin: 0 0 20px;
    padding-bottom: 12px;
    border-bottom: 2px solid ${BRAND.primary};
  }
  .info-box {
    border: 1px solid ${BRAND.border};
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 24px;
  }
  .info-row {
    display: flex;
    border-bottom: 1px solid ${BRAND.border};
  }
  .info-row:last-child { border-bottom: none; }
  .info-row .info-label {
    flex: 0 0 180px;
    background: ${BRAND.primaryTint};
    color: ${BRAND.primary};
    font-weight: 700;
    font-size: 0.78rem;
    padding: 8px 12px;
  }
  .info-row .info-value {
    flex: 1;
    font-size: 0.82rem;
    padding: 8px 12px;
  }
  .info-table-2col {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  .info-table-2col tr {
    border-bottom: 1px solid ${BRAND.border};
  }
  .info-table-2col tr:last-child {
    border-bottom: none;
  }
  .info-table-2col td {
    padding: 8px 12px;
    vertical-align: middle;
  }
  .info-table-2col td.info-label {
    width: 20%;
    background: ${BRAND.primaryTint};
    color: ${BRAND.primary};
    font-weight: 700;
    font-size: 0.78rem;
    border-right: 1px solid ${BRAND.border};
  }
  .info-table-2col td.info-value {
    width: 30%;
    font-size: 0.82rem;
    border-right: 1px solid ${BRAND.border};
  }
  .info-table-2col td.info-value:last-child {
    border-right: none;
  }
  .badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
  }
  .badge-green { background: ${BRAND.greenTint}; color: ${BRAND.green}; }
  .badge-red { background: #fbe8e9; color: ${BRAND.red}; }
  .badge-amber { background: ${BRAND.amberTint}; color: ${BRAND.amber}; }
  .badge-grey { background: #eef1f4; color: ${BRAND.muted}; }
  .section { margin-bottom: 26px; }
  h2.section-title {
    font-size: 1rem;
    font-weight: 700;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${BRAND.primary};
    margin: 0 0 12px;
  }
  table.bordered-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
    border: 1px solid ${BRAND.border};
  }
  table.bordered-table thead th {
    background: ${BRAND.primary};
    color: #fff;
    text-align: left;
    padding: 8px 10px;
    border: 1px solid ${BRAND.primaryDark};
    white-space: nowrap;
  }
  table.bordered-table tbody td {
    padding: 7px 10px;
    border: 1px solid ${BRAND.border};
  }
  table.bordered-table tbody tr:nth-child(even) { background: #f7fafc; }
  table.bordered-table tbody tr.total-row {
    background: ${BRAND.primaryTint};
    font-weight: 700;
  }
  td.amount { text-align: right; font-variant-numeric: tabular-nums; }
  tbody tr.allocation-row {
    background: ${BRAND.greenTint};
    color: ${BRAND.muted};
    font-style: italic;
  }
  td.indent { padding-left: 22px; }
  td.money-in { color: ${BRAND.green}; font-weight: 700; }
  td.money-out { color: ${BRAND.red}; font-weight: 700; }
  .table-wrap { overflow-x: auto; }
  .doc-footer {
    margin-top: 24px;
    padding-top: 12px;
    border-top: 1px solid ${BRAND.border};
    font-size: 0.72rem;
    color: ${BRAND.muted};
    text-align: center;
  }
  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    body { background: #fff; padding: 0; }
    .toolbar { display: none; }
    .sheet { box-shadow: none; border: none; padding: 0; max-width: 100%; }
    table.bordered-table { font-size: 0.62rem; }
    table.bordered-table thead th,
    table.bordered-table tbody td {
      padding: 4px 5px;
      white-space: normal;
    }
    table.detail-table { table-layout: fixed; }
    table.detail-table col.col-date { width: 11%; }
    table.detail-table col.col-desc { width: 21%; }
    table.detail-table col.col-ref { width: 13%; }
    table.detail-table col.col-money { width: 11%; }
    .info-table-2col td { padding: 4px 6px; font-size: 0.7rem; }
    .info-table-2col td.info-label { font-size: 0.68rem; }
    @page { size: A4 portrait; margin: 10mm; }
    ${extraPrintCss}
  }
</style>
</head>
<body>
  <div class="toolbar">
    <button id="btn-pdf">Download PDF</button>
    <button id="btn-csv">Download CSV</button>
  </div>

  <div class="sheet">
    <div class="doc-meta">Generated: ${generatedLabel}</div>
    <h1 class="statement-title">${escapeHtml(pageTitle)}</h1>

    <div class="info-box">
      ${infoRowsHtml}
    </div>

    ${bodyHtml}

    <div class="doc-footer">System-generated statement — ${generatedLabel} powered by Sababisha Solutions</div>
  </div>

  <script>
    document.getElementById('btn-pdf').addEventListener('click', function () {
      var btn = document.getElementById('btn-pdf');
      var originalText = btn.textContent;
      btn.textContent = 'Generating PDF...';
      btn.disabled = true;

      var sheet = document.querySelector('.sheet');
      var pdfFileName = ${JSON.stringify(pdfFileName)};

      function doDownload() {
        if (typeof html2pdf !== 'undefined') {
          sheet.classList.add('generating-pdf');
          var opt = {
            margin: [8, 8, 8, 8],
            filename: pdfFileName,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
          };
          html2pdf().set(opt).from(sheet).save().then(function() {
            sheet.classList.remove('generating-pdf');
            btn.textContent = originalText;
            btn.disabled = false;
          }).catch(function(err) {
            console.error('PDF error, falling back to print:', err);
            sheet.classList.remove('generating-pdf');
            btn.textContent = originalText;
            btn.disabled = false;
            window.print();
          });
        } else {
          btn.textContent = originalText;
          btn.disabled = false;
          window.print();
        }
      }

      if (typeof html2pdf === 'undefined') {
        setTimeout(doDownload, 500);
      } else {
        doDownload();
      }
    });

    document.getElementById('btn-csv').addEventListener('click', function () {
      var csvContent = ${JSON.stringify(csvContent)};
      var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      var url = URL.createObjectURL(blob);
      var link = document.createElement('a');
      link.href = url;
      link.download = ${JSON.stringify(csvFileName)};
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  </script>
</body>
</html>`;
};

export const infoRow = (label, valueHtml) => `
      <div class="info-row">
        <div class="info-label">${escapeHtml(label)}</div>
        <div class="info-value">${valueHtml}</div>
      </div>`;

export const infoRow2Col = (label1, valueHtml1, label2 = "", valueHtml2 = "") => `
      <tr>
        <td class="info-label">${label1 ? escapeHtml(label1) : ""}</td>
        <td class="info-value">${valueHtml1 || ""}</td>
        <td class="info-label">${label2 ? escapeHtml(label2) : ""}</td>
        <td class="info-value">${valueHtml2 || ""}</td>
      </tr>`;

export const infoTable2Col = (rowsHtml) => `
      <table class="info-table-2col">
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>`;
